import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Stack, Chip, IconButton, Alert, Snackbar,
  Card, CardContent, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Tooltip, useMediaQuery
} from '@mui/material';
import { CloudUpload, Delete, Visibility, Gavel, CheckCircle, Edit } from '@mui/icons-material';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import {
  getSkPenetapanList, createSkPenetapan, updateSkPenetapan, deleteSkPenetapan
} from '../API/skPenetapan';

const BASE_URL = process.env.REACT_APP_URL || 'http://localhost:5100';

const JENIS_SK = [
  { value: 'Rehab Rumah Tidak Layak Huni', label: 'Rehabilitasi Rumah Tidak Layak Huni' },
  { value: 'Pembangunan Rumah Layak Huni', label: 'Pembangunan Rumah Layak Huni' },
];

const jenisLabel = (v) => JENIS_SK.find(j => j.value === v)?.label || v;

const DataSkPenetapan = () => {
  const isMobile = useMediaQuery('(max-width:800px)');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [openForm, setOpenForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ jenisBantuan: JENIS_SK[0].value, nomorSK: '', tanggalSK: '', keterangan: '' });
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const handleFilePick = (f) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f || null);
    setPreviewUrl(f ? URL.createObjectURL(f) : null);
  };

  const closeForm = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFile(null);
    setOpenForm(false);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSkPenetapanList();
      setList(res.data?.data || []);
    } catch (e) {
      setSnackbar({ open: true, message: 'Gagal memuat data SK', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openAdd = () => {
    setEditItem(null);
    setForm({ jenisBantuan: JENIS_SK[0].value, nomorSK: '', tanggalSK: '', keterangan: '' });
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setOpenForm(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      jenisBantuan: item.jenisBantuan,
      nomorSK: item.nomorSK || '',
      tanggalSK: item.tanggalSK ? String(item.tanggalSK).slice(0, 10) : '',
      keterangan: item.keterangan || '',
    });
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setOpenForm(true);
  };

  const handleSave = async () => {
    if (!editItem && !file) {
      return setSnackbar({ open: true, message: 'File SK (PDF) wajib dipilih', severity: 'warning' });
    }
    const fd = new FormData();
    fd.append('jenisBantuan', form.jenisBantuan);
    fd.append('nomorSK', form.nomorSK);
    fd.append('tanggalSK', form.tanggalSK);
    fd.append('keterangan', form.keterangan);
    if (file) fd.append('fileSK', file);

    setSaving(true);
    try {
      if (editItem) {
        await updateSkPenetapan(editItem.id, fd);
        setSnackbar({ open: true, message: 'SK berhasil diperbarui', severity: 'success' });
      } else {
        await createSkPenetapan(fd);
        setSnackbar({ open: true, message: 'SK berhasil diupload', severity: 'success' });
      }
      closeForm();
      fetchList();
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.msg || 'Gagal menyimpan SK', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteSkPenetapan(confirmDelete.id);
      setSnackbar({ open: true, message: 'SK dihapus', severity: 'success' });
      setConfirmDelete(null);
      fetchList();
    } catch (e) {
      setSnackbar({ open: true, message: 'Gagal menghapus SK', severity: 'error' });
    }
  };

  const handleSetActive = async (item) => {
    try {
      const fd = new FormData();
      fd.append('isActive', 'true');
      await updateSkPenetapan(item.id, fd);
      setSnackbar({ open: true, message: `SK ${jenisLabel(item.jenisBantuan)} diaktifkan`, severity: 'success' });
      fetchList();
    } catch (e) {
      setSnackbar({ open: true, message: 'Gagal mengaktifkan SK', severity: 'error' });
    }
  };

  const contentStyle = isMobile ? {} : { marginLeft: '250px', marginRight: '20px' };

  return (
    <div className="main-content" style={contentStyle}>
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            <Gavel sx={{ mr: 1, verticalAlign: 'middle' }} /> Data SK Penetapan
          </Typography>
          <Button variant="contained" startIcon={<CloudUpload />} onClick={openAdd}>Upload SK</Button>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          SK aktif otomatis dilampirkan ke langkah Administrasi <strong>Penetapan Penerima Bantuan</strong> saat data bantuan dibuat,
          berdasarkan jenis: <strong>Rehabilitasi RTLH</strong> (sumber Questionnaire) dan <strong>Pembangunan RLH</strong> (sumber Verifikasi).
          Upload SK baru akan menggantikan SK aktif untuk jenis yang sama.
        </Alert>

        <Card elevation={2}>
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : list.length === 0 ? (
              <Alert severity="warning">Belum ada SK yang diupload.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Jenis SK</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Nomor SK</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Tanggal</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>File</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Aksi</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.map((sk) => (
                      <TableRow key={sk.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{jenisLabel(sk.jenisBantuan)}</Typography>
                          {sk.keterangan && <Typography variant="caption" color="text.secondary">{sk.keterangan}</Typography>}
                        </TableCell>
                        <TableCell>{sk.nomorSK || '-'}</TableCell>
                        <TableCell>{sk.tanggalSK ? new Date(sk.tanggalSK).toLocaleDateString('id-ID') : '-'}</TableCell>
                        <TableCell>
                          <Button size="small" startIcon={<Visibility />}
                            onClick={() => openInAppFilePreview({ url: `${BASE_URL}/sk-penetapan/file/${sk.fileSK}` })}>
                            Lihat PDF
                          </Button>
                        </TableCell>
                        <TableCell>
                          {sk.isActive
                            ? <Chip size="small" color="success" icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Aktif" />
                            : <Chip size="small" label="Nonaktif" variant="outlined" clickable onClick={() => handleSetActive(sk)} />}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit / Ganti File">
                            <IconButton size="small" onClick={() => openEdit(sk)}><Edit fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Hapus">
                            <IconButton size="small" color="error" onClick={() => setConfirmDelete(sk)}><Delete fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Form Upload / Edit */}
        <Dialog open={openForm} onClose={closeForm} maxWidth="md" fullWidth>
          <DialogTitle>{editItem ? 'Edit SK Penetapan' : 'Upload SK Penetapan'}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField select label="Jenis SK" value={form.jenisBantuan} size="small" fullWidth
                disabled={!!editItem}
                onChange={(e) => setForm(p => ({ ...p, jenisBantuan: e.target.value }))}>
                {JENIS_SK.map(j => <MenuItem key={j.value} value={j.value}>{j.label}</MenuItem>)}
              </TextField>
              <TextField label="Nomor SK" value={form.nomorSK} size="small" fullWidth
                onChange={(e) => setForm(p => ({ ...p, nomorSK: e.target.value }))} />
              <TextField label="Tanggal SK" type="date" value={form.tanggalSK} size="small" fullWidth InputLabelProps={{ shrink: true }}
                onChange={(e) => setForm(p => ({ ...p, tanggalSK: e.target.value }))} />
              <TextField label="Keterangan (opsional)" value={form.keterangan} size="small" fullWidth multiline rows={2}
                onChange={(e) => setForm(p => ({ ...p, keterangan: e.target.value }))} />
              <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
                {file ? file.name : (editItem ? 'Ganti File SK (PDF, opsional)' : 'Pilih File SK (PDF)')}
                <input hidden type="file" accept=".pdf,application/pdf" onChange={(e) => handleFilePick(e.target.files?.[0] || null)} />
              </Button>
              {file && <Typography variant="caption" color="text.secondary">{file.name} — {(file.size / 1024 / 1024).toFixed(2)} MB</Typography>}

              {/* Preview PDF file yang dipilih */}
              {previewUrl && (
                <Box sx={{ border: '1px solid #ddd', borderRadius: 1.5, overflow: 'hidden' }}>
                  <Typography variant="caption" fontWeight={600} sx={{ display: 'block', px: 1.5, py: 0.75, bgcolor: '#f5f5f5' }}>
                    Preview SK
                  </Typography>
                  <object data={previewUrl} type="application/pdf" width="100%" height={420}>
                    <Typography variant="caption" sx={{ p: 2, display: 'block' }}>
                      Preview PDF tidak tersedia di browser ini.{' '}
                      <Button size="small" onClick={() => window.open(previewUrl, '_blank')}>Buka di tab baru</Button>
                    </Typography>
                  </object>
                </Box>
              )}

              {/* Preview SK yang sudah tersimpan (mode edit) */}
              {!file && editItem?.fileSK && (
                <Box>
                  <Button size="small" startIcon={<Visibility />}
                    onClick={() => openInAppFilePreview({ url: `${BASE_URL}/sk-penetapan/file/${editItem.fileSK}` })}>
                    Lihat SK Saat Ini
                  </Button>
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeForm}>Batal</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Konfirmasi hapus */}
        <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
          <DialogTitle>Hapus SK?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Hapus SK <strong>{confirmDelete && jenisLabel(confirmDelete.jenisBantuan)}</strong>
              {confirmDelete?.nomorSK ? ` (${confirmDelete.nomorSK})` : ''}? File PDF juga akan dihapus.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)}>Batal</Button>
            <Button color="error" variant="contained" onClick={handleDelete}>Hapus</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
};

export default DataSkPenetapan;
